import { useEffect, useRef, useState } from 'react'

/**
 * StrictMode in React 18 causes redundant unmount. This hook makes a code inside useEffect to be executed just once
 */
export const useEffectOnce = (effect: () => void | (() => void)) => {
  const effectFunction = useRef<() => void | (() => void)>(effect)
  const destroyFunction = useRef<void | (() => void)>()
  const effectCalled = useRef(false)
  const rendered = useRef(false)
  const [, setValue] = useState<number>(0)

  if (effectCalled.current) {
    rendered.current = true
  }

  useEffect(() => {
    // Only execute the effect first time around
    if (!effectCalled.current) {
      destroyFunction.current = effectFunction.current()
      effectCalled.current = true
    }

    // This forces one render after the effect is run
    setValue((value) => value + 1)

    return () => {
      // If the comp didn't render since the useEffect was called,
      // We know it's the dummy React cycle
      if (!rendered.current) {
        return
      }

      // Otherwise this is not a dummy destroy, so call the destroy func
      if (destroyFunction.current) {
        destroyFunction.current()
      }
    }
  }, [])
}
